import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NewOrderType } from 'types/order';

const useStyles = makeStyles((theme: any) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    marginBottom: 0,
    boxShadow: theme.shadows[1],
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    letterSpacing: '0.5px',
  },
  roleSection: {
    padding: theme.spacing(1),
  },
  roleTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    letterSpacing: '0.4px',
  },
  participantList: {
    padding: 0,
    marginLeft: theme.spacing(1),
  },
  participantItem: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: 0,
    paddingRight: 0,
  },
  participantName: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
  },
  emptyState: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
}));

interface ParticipantsCardProps {
  order: NewOrderType;
}

/**
 * ParticipantsCard - Displays participants grouped by role
 *
 * Shows buyers, sellers, and lenders from order.parties[] array,
 * grouped by role with alphabetical sorting within each group.
 */
const ParticipantsCard: React.FC<ParticipantsCardProps> = ({ order }) => {
  const { t } = useTranslation('closingDisclosure');
  const classes = useStyles();

  // Extract and sort participants by role
  const participantsByRole = useMemo(() => {
    if (!order?.parties || !Array.isArray(order.parties)) {
      return {
        buyers: [],
        sellers: [],
        lenders: [],
      };
    }

    const buyers: string[] = [];
    const sellers: string[] = [];
    const lenders: string[] = [];

    order.parties.forEach((party) => {
      if (!party.name || !Array.isArray(party.kinds)) return;

      // Check kinds array (case-insensitive)
      const kindsLower = party.kinds.map((k) => (k || '').toLowerCase());

      if (kindsLower.includes('buyer')) {
        buyers.push(party.name);
      }
      if (kindsLower.includes('seller')) {
        sellers.push(party.name);
      }
      if (kindsLower.includes('lender')) {
        lenders.push(party.name);
      }
    });

    // Sort each group alphabetically
    const sortedBuyers = buyers.sort((a, b) =>
      (a || '').localeCompare(b || '', undefined, { numeric: true })
    );
    const sortedSellers = sellers.sort((a, b) =>
      (a || '').localeCompare(b || '', undefined, { numeric: true })
    );
    const sortedLenders = lenders.sort((a, b) =>
      (a || '').localeCompare(b || '', undefined, { numeric: true })
    );

    return {
      buyers: sortedBuyers,
      sellers: sortedSellers,
      lenders: sortedLenders,
    };
  }, [order]);

  // Check if there are any participants
  const hasParticipants =
    participantsByRole.buyers.length > 0 ||
    participantsByRole.sellers.length > 0 ||
    participantsByRole.lenders.length > 0;

  // Count how many roles have participants
  const activeRolesCount = [
    participantsByRole.buyers.length > 0,
    participantsByRole.sellers.length > 0,
    participantsByRole.lenders.length > 0,
  ].filter(Boolean).length;

  // Calculate responsive grid width based on active roles
  const getGridWidth = () => {
    switch (activeRolesCount) {
      case 3:
        return { md: 4, sm: 6, xs: 12 }; // 33% on desktop, 50% on tablet, 100% on mobile
      case 2:
        return { md: 6, sm: 6, xs: 12 }; // 50% on desktop/tablet, 100% on mobile
      case 1:
        return { md: 12, sm: 12, xs: 12 }; // 100% on all screens
      default:
        return { md: 12, sm: 12, xs: 12 };
    }
  };

  const gridWidth = getGridWidth();

  const renderRoleColumn = (
    title: string,
    names: string[],
    roleKey: string,
    gridSize: { md: number; sm: number; xs: number }
  ) => {
    if (names.length === 0) return null;

    return (
      <Grid
        item
        xs={gridSize.xs as any}
        sm={gridSize.sm as any}
        md={gridSize.md as any}
        key={roleKey}
      >
        <Box className={classes.roleSection}>
          <Typography className={classes.roleTitle}>{title}</Typography>
          <List className={classes.participantList}>
            {names.map((name) => (
              <ListItem
                key={`${roleKey}-${name}`}
                className={classes.participantItem}
              >
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{
                    className: classes.participantName,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Grid>
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h6" className={classes.cardTitle}>
          {t('participants-title')}
        </Typography>

        {!hasParticipants ? (
          <Typography className={classes.emptyState}>
            {t('participants-empty')}
          </Typography>
        ) : (
          <Grid container spacing={2} direction="row">
            {renderRoleColumn(
              t('participants-buyers'),
              participantsByRole.buyers,
              'buyers',
              gridWidth
            )}
            {renderRoleColumn(
              t('participants-sellers'),
              participantsByRole.sellers,
              'sellers',
              gridWidth
            )}
            {renderRoleColumn(
              t('participants-lenders'),
              participantsByRole.lenders,
              'lenders',
              gridWidth
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantsCard;
